import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@finn/design-system/atoms/table';
import { useMediaQuery } from '@finn/design-system/helpers/media';
import { Features, useGetVariant } from '@finn/ua-featureflags';
import { ModalKey, useOpenModal } from '@finn/ua-modals';
import { interactionTrackingEvent, TrackingEventName } from '@finn/ua-tracking';
import { useShallowRouter } from '@finn/ua-vehicle';
import {
  App,
  cn,
  trackEvent,
  useCurrentApp,
  useCurrentLocale,
} from '@finn/ui-utils';
import { useCallback, useContext, useEffect, useState } from 'react';

import { GenericVehicleDetails, getModelPagePath } from '../../../core';
import { useConfigureStore } from '../../../pdp-management';
import { VehicleContext } from '../../../product-cards';
import { DesktopGroupedProduct } from './DesktopGroupedProduct';
import { MobileGroupedProduct } from './MobileGroupedProduct';
import { useGroupedProducts } from './useGroupedProducts';

type Props = {
  openPCP?: () => unknown;
  closedUserGroupId?: string;
  isPDPHelperEnabled?: boolean;
};
export const GroupComparison = ({
  openPCP,
  closedUserGroupId,
  isPDPHelperEnabled,
}: Props) => {
  const { vehicle, groupedProducts: initialGroupedProducts } =
    useContext(VehicleContext);
  const { setVehicle: setPCPVehicle } = useConfigureStore();
  const { replace } = useShallowRouter();
  const { setInfoDrawerVehicle } = useContext(VehicleContext);
  const { locale } = useCurrentLocale();
  const isOutOfStock = !vehicle?.availability;
  const openModal = useOpenModal();
  // TODO: remove for advanced grouping rollout in B2B
  const isB2BApp = useCurrentApp() === App.B2B;
  const { products, updateColorOption, isLoadingProduct, isLoading } =
    useGroupedProducts({
      initialProduct: vehicle,
      initialGroupedProducts,
    });

  useEffect(() => {
    if (!products?.length) {
      // Reload without query params
      replace(window.location.pathname, { scroll: false });
    }
  }, [products]);

  const [selectedConfig, setSelectedConfig] = useState<number>();

  const addConfigToURL = useCallback(
    (productId: string, selectedConfigId: number) => {
      const pathname = `/${isB2BApp ? `product/${productId}` : `models/${getModelPagePath(vehicle)}`}`;

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('selected_config', selectedConfigId.toString());

      replace(`${pathname}?${searchParams.toString()}`, { scroll: false });
    },
    [replace, vehicle?.id, isB2BApp]
  );

  const handleOpenPCP = useCallback(
    (selectedVehicle: GenericVehicleDetails, idx: number) => {
      setPCPVehicle(selectedVehicle, locale, false);

      addConfigToURL(selectedVehicle.id, selectedVehicle.config_id);
      openPCP?.();

      interactionTrackingEvent(TrackingEventName.CTA_CLICKED, {
        location: 'table',
        productId: selectedVehicle.id,
        productInfo: {
          position: idx,
        },
      });
    },
    [locale, openPCP, setPCPVehicle, addConfigToURL]
  );

  const handleMoreInformationClick = useCallback(
    (selectedVehicle: GenericVehicleDetails, idx: number) => {
      addConfigToURL(selectedVehicle.id, selectedVehicle.config_id);

      interactionTrackingEvent(TrackingEventName.MODAL_OPENED, {
        name: 'carinfo',
        configId: selectedVehicle.config_id,
        productInfo: {
          position: idx,
        },
      });
      setPCPVehicle(selectedVehicle, locale, false);
      setInfoDrawerVehicle(selectedVehicle);
      openModal(ModalKey.PDP_CAR_INFO);
    },
    [locale, openModal, setInfoDrawerVehicle, addConfigToURL]
  );

  const handleOnColorChange = useCallback(
    async (rowNumber: number, newConfigId: number) => {
      const product = await updateColorOption(rowNumber, newConfigId);
      addConfigToURL(product.id, newConfigId);
    },
    [locale, updateColorOption, addConfigToURL]
  );

  const handleImageClick = (product: GenericVehicleDetails) => {
    trackEvent(TrackingEventName.CONFIG_IMAGE_CLICKED, {
      configId: product.config_id,
      vehicleId: product.id,
    });
  };

  const isMobile = useMediaQuery('md');

  // Only show the label if there are different labels across equipment lines
  const showLabel = products?.some(
    (product) =>
      product.product_label.label !== products?.[0].product_label.label
  );

  // Highlight the selected config in the table
  useEffect(() => {
    // Only change the color if the products are loaded
    if (isLoading) return;

    const url = new URL(window.location.href);
    const configId = url.searchParams.get('selected_config');
    if (!configId) return;

    setSelectedConfig(parseInt(configId));

    const parentProduct = products?.find(({ color_list }) =>
      color_list.some(({ uid }) => uid === Number(configId))
    );
    if (!parentProduct) {
      return;
    }

    const prefix = isMobile ? 'mobile-' : 'desktop-';
    const row = document.querySelector(`#${prefix}${parentProduct.config_id}`);
    if (!row) {
      return;
    }

    const idx = row.getAttribute('data-idx');
    if (!idx) return;

    // Change the color if the selected config is not the same as the product config
    const product = products?.[parseInt(idx)];
    if (product?.config_id !== parseInt(configId)) {
      handleOnColorChange(parseInt(idx), parseInt(configId));
    }

    // Scroll down to the product
    setTimeout(() => {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 500);
  }, [isMobile, isLoading]);

  const differentiateConfigsExp = useGetVariant(
    Features.ExpPdpDifferentiateConfigs
  );
  if (isOutOfStock || !products?.length) {
    return null;
  }

  const showDiff = differentiateConfigsExp === 'b' && products?.length > 1;

  return (
    <div className="container pt-20">
      <h2 id="group-comparison" className="mb-6 pt-4 web-t4-semibold">
        Verfügbare Konfigurationen
      </h2>

      <Table
        data-testid="group-comparison-desktop"
        className="hidden md:block"
        containerClassname="!overflow-visible"
      >
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/5 px-4">Ausstattungslinie</TableHead>
            <TableHead className="w-24 px-2">Farbe</TableHead>
            <TableHead className="w-[140px] px-2">Verfügbarkeit</TableHead>
            <TableHead className="w-20 px-2">Kraftstoff</TableHead>
            <TableHead className="w-20 px-2">Leistung</TableHead>
            <TableHead className="w-[100px] px-2">Antriebsart</TableHead>
            <TableHead className="w-[120px] px-2">Preis pro Monat</TableHead>
            <TableHead className="w-[60px] px-2" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product, idx) => (
            <DesktopGroupedProduct
              selectedConfig={selectedConfig}
              key={product.id}
              idx={idx}
              product={product}
              showDiff={showDiff}
              closedUserGroupId={closedUserGroupId}
              handleOpenPCP={() => handleOpenPCP(product, idx)}
              handleOnColorChange={(id) => handleOnColorChange(idx, id)}
              handleMoreInformationClick={() =>
                handleMoreInformationClick(product, idx)
              }
              handleImageClick={handleImageClick}
              isLoading={isLoadingProduct[idx]}
              isPDPHelperEnabled={isPDPHelperEnabled}
              showLabel={showLabel}
            />
          ))}
        </TableBody>
      </Table>

      <div
        data-testid="group-comparison-mobile"
        className={cn(
          'grid rounded border-l border-t border-pearl md:hidden',
          products.length > 1 ? 'sm:grid-cols-2' : 'sm:grid-cols-1'
        )}
      >
        {products.map((product, idx) => (
          <MobileGroupedProduct
            selectedConfig={selectedConfig}
            key={product.id}
            idx={idx}
            product={product}
            showDiff={showDiff}
            closedUserGroupId={closedUserGroupId}
            handleOpenPCP={() => handleOpenPCP(product, idx)}
            handleOnColorChange={(id) => handleOnColorChange(idx, id)}
            handleMoreInformationClick={() =>
              handleMoreInformationClick(product, idx)
            }
            handleImageClick={handleImageClick}
            isLoading={isLoadingProduct[idx]}
            isPDPHelperEnabled={isPDPHelperEnabled}
            showLabel={showLabel}
          />
        ))}
      </div>
    </div>
  );
};
